import React, { useState, useEffect, useCallback } from 'react';

const CATEGORY_ORDER = ['Auth', 'Stripe', 'Email', 'App', 'Push', 'Security', 'Integrations', 'Workers'];
const CATEGORY_ICONS = {
  Auth:         'fa-solid fa-lock',
  Stripe:       'fa-brands fa-stripe-s',
  Email:        'fa-solid fa-envelope',
  App:          'fa-solid fa-globe',
  Push:         'fa-solid fa-bell',
  Security:     'fa-solid fa-shield-halved',
  Integrations: 'fa-solid fa-plug',
  Workers:      'fa-solid fa-gears',
};

export default function AdminSettings({ apiFetch, token, toast }) {
  const [settings, setSettings]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [activeTab, setActiveTab]   = useState('Auth');
  const [edits, setEdits]           = useState({});      // { KEY: newValue }
  const [revealed, setRevealed]     = useState({});      // { KEY: true } — show plaintext
  const [saving, setSaving]         = useState({});      // { KEY: true }
  const [reloading, setReloading]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/admin/settings');
      const data = await res.json();
      setSettings(data.settings || []);
    } catch {
      toast.error('Error', 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, toast]);

  useEffect(() => { load(); }, [load]);

  const grouped = CATEGORY_ORDER.reduce((acc, cat) => {
    acc[cat] = settings.filter(s => s.category === cat);
    return acc;
  }, {});

  const handleSave = async (key) => {
    const meta = settings.find(s => s.key === key);
    if (!meta) return;
    const value = edits[key];
    if (value === undefined || value === '') return;
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      const res = await apiFetch(`/api/admin/settings/${key}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      toast.success('Saved', `${meta.label} updated successfully`);
      // Clear edit, reload to get fresh masked value
      setEdits(prev => { const n = { ...prev }; delete n[key]; return n; });
      await load();
    } catch (err) {
      toast.error('Error', err.message);
    } finally {
      setSaving(prev => { const n = { ...prev }; delete n[key]; return n; });
    }
  };

  const handleReload = async () => {
    setReloading(true);
    try {
      const res = await apiFetch('/api/admin/settings/reload', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      toast.success('Reloaded', 'All settings reloaded and services reinitialized');
    } catch (err) {
      toast.error('Error', err.message);
    } finally {
      setReloading(false);
    }
  };

  const displayValue = (s) => {
    if (edits[s.key] !== undefined) return edits[s.key];
    if (s.sensitive) return '';  // always blank for sensitive — user must type new value
    return s.value || '';
  };

  const placeholder = (s) => {
    if (s.sensitive) return s.isSet ? '(encrypted — type new value to change)' : '(not set)';
    return s.isSet ? '' : '(not set)';
  };

  if (loading) {
    return (
      <div className="admin-card" style={{ padding: '32px', textAlign: 'center' }}>
        <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '24px', color: 'var(--admin-text-muted)' }} />
        <p style={{ color: 'var(--admin-text-secondary)', marginTop: '12px', fontSize: '13px' }}>Loading settings...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="admin-flex-between" style={{ marginBottom: '20px' }}>
        <div>
          <p style={{ color: 'var(--admin-text-secondary)', fontSize: '13px', marginTop: '4px' }}>
            All sensitive values are stored AES-256-GCM encrypted in the database.
            Changes apply immediately — no restart required.
          </p>
        </div>
        <button
          className="admin-btn admin-btn--secondary"
          onClick={handleReload}
          disabled={reloading}
          title="Force reload all settings from DB and reinitialize services"
        >
          <i className={`fa-solid ${reloading ? 'fa-spinner fa-spin' : 'fa-rotate'}`} style={{ marginRight: '6px' }} />
          {reloading ? 'Reloading...' : 'Force Reload'}
        </button>
      </div>

      {/* Category tabs */}
      <div className="admin-tabs" style={{ marginBottom: '20px', flexWrap: 'wrap', gap: '4px' }}>
        {CATEGORY_ORDER.filter(cat => (grouped[cat] || []).length > 0).map(cat => (
          <button
            key={cat}
            className={`admin-tab${activeTab === cat ? ' admin-tab--active' : ''}`}
            onClick={() => setActiveTab(cat)}
          >
            <i className={`${CATEGORY_ICONS[cat] || 'fa-solid fa-circle'}`} style={{ marginRight: '6px', fontSize: '11px' }} />
            {cat}
          </button>
        ))}
      </div>

      {/* Settings for active category */}
      <div className="admin-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <i className={`${CATEGORY_ICONS[activeTab] || 'fa-solid fa-circle'}`} style={{ fontSize: '16px', color: 'var(--admin-text-muted)' }} />
          <h3 className="admin-card-title" style={{ margin: 0 }}>{activeTab} Settings</h3>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {(grouped[activeTab] || []).map(s => (
            <div key={s.key} style={{
              borderBottom: '1px solid var(--admin-border)',
              paddingBottom: '20px',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1', minWidth: '220px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontWeight: '600', fontSize: '13px', color: 'var(--admin-text-primary)' }}>{s.label}</span>
                    {s.sensitive && (
                      <span style={{
                        fontSize: '10px', fontWeight: '600', letterSpacing: '0.05em',
                        padding: '1px 6px', borderRadius: '4px',
                        background: 'rgba(var(--admin-warning-rgb, 255,168,0),0.12)',
                        color: 'var(--admin-warning, #ffa800)',
                      }}>ENCRYPTED</span>
                    )}
                    {s.isSet && !s.sensitive && (
                      <span style={{
                        fontSize: '10px', fontWeight: '600', letterSpacing: '0.05em',
                        padding: '1px 6px', borderRadius: '4px',
                        background: 'rgba(var(--admin-success-rgb,34,197,94),0.12)',
                        color: 'var(--admin-success,#22c55e)',
                      }}>SET</span>
                    )}
                    {!s.isSet && (
                      <span style={{
                        fontSize: '10px', fontWeight: '600', letterSpacing: '0.05em',
                        padding: '1px 6px', borderRadius: '4px',
                        background: 'rgba(var(--admin-error-rgb,239,68,68),0.12)',
                        color: 'var(--admin-error,#ef4444)',
                      }}>NOT SET</span>
                    )}
                  </div>
                  <code style={{ fontSize: '11px', color: 'var(--admin-text-muted)', display: 'block', marginBottom: '4px' }}>{s.key}</code>
                  <p style={{ fontSize: '12px', color: 'var(--admin-text-secondary)', margin: 0, lineHeight: '1.5' }}>{s.description}</p>
                </div>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flex: '1', minWidth: '280px', maxWidth: '480px' }}>
                  <div style={{ flex: 1, position: 'relative' }}>
                    {s.type === 'boolean' ? (
                      <select
                        className="admin-input"
                        value={edits[s.key] !== undefined ? edits[s.key] : (s.value || '')}
                        onChange={e => setEdits(prev => ({ ...prev, [s.key]: e.target.value }))}
                        style={{ fontSize: '13px' }}
                      >
                        <option value="">— select —</option>
                        <option value="true">true</option>
                        <option value="false">false</option>
                      </select>
                    ) : (
                      <input
                        className="admin-input"
                        type={s.sensitive && !revealed[s.key] ? 'password' : 'text'}
                        value={displayValue(s)}
                        onChange={e => setEdits(prev => ({ ...prev, [s.key]: e.target.value }))}
                        placeholder={placeholder(s)}
                        style={{ fontSize: '13px', paddingRight: s.sensitive ? '36px' : undefined }}
                        autoComplete="off"
                        spellCheck={false}
                      />
                    )}
                    {s.sensitive && s.type !== 'boolean' && (
                      <button
                        type="button"
                        onClick={() => setRevealed(prev => ({ ...prev, [s.key]: !prev[s.key] }))}
                        style={{
                          position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--admin-text-muted)', fontSize: '13px', padding: '2px',
                        }}
                        title={revealed[s.key] ? 'Hide' : 'Reveal'}
                      >
                        <i className={`fa-solid ${revealed[s.key] ? 'fa-eye-slash' : 'fa-eye'}`} />
                      </button>
                    )}
                  </div>
                  <button
                    className="admin-btn admin-btn--primary admin-btn--sm"
                    onClick={() => handleSave(s.key)}
                    disabled={saving[s.key] || edits[s.key] === undefined || edits[s.key] === ''}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {saving[s.key] ? <i className="fa-solid fa-spinner fa-spin" /> : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
